import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import type { FakKit, RetourRemplacement } from '../../types';

import { dePersonne } from '../messages/format';
/**
 * Mettre à jour SA fiche relais (FRA) à partir du retour d'une personne qui a remplacé — par-dessus la fiche de la demande,
 * avec le retour AFFICHÉ À CÔTÉ (jamais une navigation vers l'atelier : on garde le retour sous les yeux). La personne décide
 * seule de ce qu'elle en retient. Mêmes droits et mêmes règles de publication que l'édition depuis « Ateliers » (PUT /api/fak).
 */
export function FraEdition({ atelierId, atelierNom, retour, qui, onClose }: {
  atelierId: number; atelierNom: string; retour: RetourRemplacement; qui: string; onClose: () => void;
}) {
  const [fak, setFak] = useState<FakKit | null>(null);
  const [peutModifier, setPeutModifier] = useState(false);
  const [chargement, setChargement] = useState(true);
  const [contenu, setContenu] = useState('');
  const [echelle, setEchelle] = useState('');
  const [lien, setLien] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enregistre, setEnregistre] = useState(false);
  const [avertissements, setAvertissements] = useState<string[]>([]);

  useEffect(() => {
    const surEchap = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', surEchap);
    return () => document.removeEventListener('keydown', surEchap);
  }, [onClose]);

  useEffect(() => {
    let annule = false;
    api.getFak(atelierId)
      .then((d) => {
        if (annule) return;
        setFak(d.fak); setPeutModifier(d.peut_modifier);
        setContenu(d.fak?.contenu ?? ''); setEchelle(d.fak?.echelle_difficulte ?? ''); setLien(d.fak?.lien_document ?? '');
      })
      .catch((err: unknown) => { if (!annule) setErreur(err instanceof Error ? err.message : 'Impossible de charger la fiche.'); })
      .finally(() => { if (!annule) setChargement(false); });
    return () => { annule = true; };
  }, [atelierId]);

  async function enregistrer() {
    setEnCours(true); setErreur(null); setEnregistre(false);
    try {
      const res = await api.upsertFak(atelierId, {
        statut: fak?.statut ?? 'brouillon', contenu, echelle_difficulte: echelle || undefined, lien_document: lien || undefined,
      }) as FakKit & { avertissements?: string[] };
      setFak(res); setAvertissements(res.avertissements ?? []); setEnregistre(true);
    } catch (err) {
      setErreur(err instanceof Error ? err.message : "La fiche n'a pas pu être enregistrée.");
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" role="dialog" aria-modal="true" aria-labelledby="fra-edition-titre" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title" id="fra-edition-titre">Mettre à jour la FRA · {atelierNom}</h3>
          <button type="button" className="close-btn" onClick={onClose} aria-label="Fermer">✕</button>
        </div>
        <div className="modal-body fra-edition">
          <section aria-label="Votre fiche relais">
            <h4 className="retour-h">Votre fiche</h4>
            {chargement ? <div className="spinner" /> : (
              <>
                {!peutModifier && (
                  <p className="text-sm text-warn" role="status">Vous ne pouvez pas modifier cette fiche : demandez à l'équipe gestion de le faire avec ce retour.</p>
                )}
                <div className="form-group">
                  <label className="form-label" htmlFor="fra-contenu">Contenu (3 à 6 thématiques, une par titre ou par puce)</label>
                  <textarea id="fra-contenu" className="form-textarea" rows={9} value={contenu} disabled={!peutModifier}
                    onChange={(e) => { setContenu(e.target.value); setEnregistre(false); }} />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="fra-echelle">Échelle de difficulté</label>
                  <input id="fra-echelle" className="form-input" value={echelle} disabled={!peutModifier}
                    onChange={(e) => { setEchelle(e.target.value); setEnregistre(false); }} />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="fra-lien">Lien du document complet</label>
                  <input id="fra-lien" className="form-input" type="url" value={lien} disabled={!peutModifier}
                    onChange={(e) => { setLien(e.target.value); setEnregistre(false); }} />
                </div>
              </>
            )}
          </section>
          <aside className="fra-edition-retour" aria-label={`Retour ${dePersonne(qui)}`}>
            <h4 className="retour-h">Le retour {dePersonne(qui)}</h4>
            {retour.faits_marquants && (<><div className="retour-h2">Faits marquants</div><p className="retour-texte">{retour.faits_marquants}</p></>)}
            {retour.protocoles && (<><div className="retour-h2">Protocoles appliqués</div><p className="retour-texte">{retour.protocoles}</p></>)}
          </aside>
        </div>
        {erreur && <div className="badge badge-err" role="alert" style={{ display: 'block', margin: '0 var(--s16) var(--s8)', textTransform: 'none', letterSpacing: 'normal' }}>{erreur}</div>}
        {enregistre && <div className="text-sm" role="status" style={{ color: 'var(--ok)', margin: '0 var(--s16) var(--s8)' }}>✓ Fiche enregistrée.{avertissements.length > 0 ? ` ${avertissements.join(' ')}` : ''}</div>}
        <div className="modal-footer">
          <button type="button" className="btn btn-sm" onClick={onClose}>Fermer</button>
          <button type="button" className="btn btn-sm btn-accent" disabled={enCours || chargement || !peutModifier} onClick={enregistrer}>
            {enCours ? <span className="spinner spinner-sm" /> : 'Enregistrer la fiche'}
          </button>
        </div>
      </div>
    </div>
  );
}
